import { useEffect, useMemo, useRef } from 'react';

import ComponentSelect from './Components/ComponentSelect/ComponentSelect';
import ContactUs from './Components/ContactUs/ContactUs';
import Gen1 from './Components/Gen1/Gen1';
import ProtectionPlan from './Components/ProtectionPlan/ProtectionPlan';
import History from './Components/Shipping/History/History';
import Request from './Components/Shipping/NewShipment/Request';
import UserSetting from './Components/UserSetting/UserSetting';
import * as Constant from './Constants/Constant';
import { useAppContext } from './Contexts/AppContext';
import {
    getAllowedSetForUser,
    getFirstAllowedTabStatus,
    resolveTabFallback,
} from './Menu/menuConfig';

export default function Tab() {
    const { Reducer, Actions } = useAppContext();
    const prevReducer_masterDataIsLoading = useRef(Reducer.masterDataIsLoading);

    const hasUserInfo =
        Reducer?.userInformation &&
        Object.keys(Reducer.userInformation).length > 0;
    const allowedSet = useMemo(
        () =>
            hasUserInfo ? getAllowedSetForUser(Reducer.userInformation) : null,
        [
            hasUserInfo,
            Reducer.userInformation?.RepairCenterFlag,
            Reducer.userInformation?.AllowedTabStatuses?.join('|'),
        ]
    );
    const isComponentSelectForNonRepairCenter =
        Reducer.tabStatus === Constant.REPAIRREQUEST_STAT_SELECT_COMPONENT &&
        !Reducer.userInformation?.RepairCenterFlag;
    const isAllowed = allowedSet
        ? allowedSet.has(Reducer.tabStatus) ||
          isComponentSelectForNonRepairCenter
        : true;

    useEffect(() => {
        Actions.masterDataFetch(Reducer.csrftoken);
    }, []);

    useEffect(() => {
        // Guard: prevent access to tabs not allowed for this user
        if (
            !Reducer?.userInformation ||
            Object.keys(Reducer.userInformation).length === 0
        )
            return;
        const currentAllowedSet = allowedSet;
        if (!currentAllowedSet || currentAllowedSet.size === 0) return;

        // Component Select is a hub screen for non-repair-center users only.
        if (
            Reducer.tabStatus === Constant.REPAIRREQUEST_STAT_SELECT_COMPONENT
        ) {
            if (Reducer.userInformation?.RepairCenterFlag) {
                const firstAllowed = getFirstAllowedTabStatus(
                    Reducer.userInformation,
                    null
                );
                const nextTab =
                    firstAllowed ===
                    Constant.REPAIRREQUEST_STAT_SELECT_COMPONENT
                        ? null
                        : firstAllowed;
                if (nextTab && nextTab !== Reducer.tabStatus) {
                    Actions.setTabStatus(nextTab);
                }
            }
            return;
        }

        if (!currentAllowedSet.has(Reducer.tabStatus)) {
            const fallback = resolveTabFallback(
                Reducer.userInformation,
                Reducer.tabStatus
            );
            if (fallback && fallback !== Reducer.tabStatus) {
                Actions.setTabStatus(fallback);
                return;
            }
            const firstAllowed = getFirstAllowedTabStatus(
                Reducer.userInformation,
                null
            );
            if (firstAllowed && firstAllowed !== Reducer.tabStatus) {
                Actions.setTabStatus(firstAllowed);
            }
        }
    }, [
        Reducer.tabStatus,
        Reducer.userInformation?.UserId,
        Reducer.userInformation?.CustomerNumber,
        Reducer.userInformation?.ManagerFlag,
        Reducer.userInformation?.RepairCenterFlag,
        Reducer.userInformation?.AllowedTabStatuses?.join('|'),
    ]);

    useEffect(() => {
        if (
            prevReducer_masterDataIsLoading.current &&
            !Reducer.masterDataIsLoading &&
            !Reducer.masterDataIsLoadingError
        ) {
            Actions.setDisclaimer(Reducer.userInformation.FirstLogin);
        }
        prevReducer_masterDataIsLoading.current = Reducer.masterDataIsLoading;
    }, [Reducer.masterDataIsLoading, Reducer.masterDataIsLoadingError]);

    let component;
    if (allowedSet && allowedSet.size === 0) {
        component = <div>No permissions are configured.</div>;
    } else if (allowedSet && !isAllowed) {
        component = <div>You do not have access to this menu.</div>;
    } else {
        switch (Reducer.tabStatus) {
            case Constant.REPAIRREQUEST_STAT_NEW_SHIPMENT:
                component = <Request />;
                break;
            case Constant.REPAIRREQUEST_STAT_HISTORY:
                component = <History />;
                break;
            case Constant.REPAIRREQUEST_STAT_PROTECTIONPLAN_HISTORY:
            case Constant.REPAIRREQUEST_STAT_PROTECTIONPLAN_REGISTER:
                component = <ProtectionPlan />;
                break;
            case Constant.REPAIRREQUEST_STAT_GEN1_APPLICATION:
            case Constant.REPAIRREQUEST_STAT_GEN1_HISTORY:
                component = <Gen1 />;
                break;
            case Constant.REPAIRREQUEST_STAT_USERSETTING:
                component = <UserSetting />;
                break;
            case Constant.REPAIRREQUEST_STAT_CONATCTUS:
                component = <ContactUs />;
                break;
            case Constant.REPAIRREQUEST_STAT_SELECT_COMPONENT:
                component = <ComponentSelect />;
                break;
            default:
                component = <div>No content...</div>;
                break;
        }
    }

    return <div className="App">{component}</div>;
}
